import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAbsenceStore, BALANCE_WARNING_CODES } from '../../store/useAbsenceStore';
import { useVacationStore } from '../../store/useVacationStore';
import { useOvertimeStore } from '../../store/useOvertimeStore';
import { parseAxiosError } from '../../errors';
import { useAbsenceTypeStore } from '../../store/useAbsenceTypeStore';
import { useEmployeeStore } from '../../store/useEmployeeStore';
import { useAuthStore } from '../../store/useAuthStore';
import { Card } from '../../components/Shared/Card';
import { Button } from '../../components/Shared/Button';
import { Loading } from '../../components/Shared/Loading';
import { EmptyState } from '../../components/Shared/EmptyState';
import { Badge } from '../../components/Shared/Badge';
import { isBackOfficeRole } from '../../utils/roles';
import { SlideOver, SlideOverFooter } from '../../components/Shared/SlideOver';
import { ConfirmDialog } from '../../components/Shared/ConfirmDialog';
import { FormField } from '../../components/Shared/FormField';
import { TextArea } from '../../components/Shared/TextArea';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '../../components/Shared/Table';
import { Modal } from '../../components/Shared/Modal';
import { Calendar, Plus, Search, Pencil, Trash2, Check, X, AlertTriangle, ExternalLink, FileWarning } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CertificateCell } from './components/CertificateCell';
import { SubstitutePickerDialog } from './components/SubstitutePickerDialog';
import { WorkingDaysPreview } from './components/WorkingDaysPreview';
import { RoundedDaysHint } from './components/RoundedDaysHint';
import { useWorkingDaysPreview } from '../../hooks/useWorkingDaysPreview';
import { chargeableDays, formatDays } from '../../utils/absenceDays';
import type {
  AbsenceListDto,
  CreateAbsenceRequest,
  UpdateAbsenceRequest,
  ApproveAbsenceResult,
  AbsenceSubstitutionResult,
} from '../../types';

/** Hours as a signed h:mm string, e.g. +12:30 or −3:15. */
function formatSignedHours(hours: number): string {
  const sign = hours < 0 ? '−' : '+';
  const minutes = Math.round(Math.abs(hours) * 60);
  return `${sign}${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
}

export default function AbsencesPage() {
  const { t } = useTranslation(['absences', 'common']);
  const navigate = useNavigate();
  const { employee } = useAuthStore();
  const {
    absences,
    error,
    fetchAbsences,
    getAbsenceById,
    createAbsence,
    updateAbsence,
    deleteAbsence,
    approveAbsence,
    uploadCertificate,
    openCertificate,
    uploadingCertificateFor,
    assignSubstitute,
  } = useAbsenceStore();
  const { absenceTypes, fetchAbsenceTypes } = useAbsenceTypeStore();
  const { employees, fetchEmployees } = useEmployeeStore();

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCertificate, setFilterCertificate] = useState<string>('all');

  // SlideOver state
  const [isSlideOverOpen, setIsSlideOverOpen] = useState(false);
  const [editingAbsence, setEditingAbsence] = useState<AbsenceListDto | null>(null);

  // Delete dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [absenceToDelete, setAbsenceToDelete] = useState<{ id: string; name: string } | null>(null);

  // Affected entries dialog (after approval)
  const [approveResult, setApproveResult] = useState<ApproveAbsenceResult | null>(null);
  const [isAffectedDialogOpen, setIsAffectedDialogOpen] = useState(false);

  // Vertretung: beim Genehmigen und nachträglich (ADR-0016)
  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
  const [absenceToApprove, setAbsenceToApprove] = useState<AbsenceListDto | null>(null);
  const [isSubstituteDialogOpen, setIsSubstituteDialogOpen] = useState(false);
  const [absenceForSubstitute, setAbsenceForSubstitute] = useState<AbsenceListDto | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  // Ergebnis einer nachträglichen Zuweisung — ohne Genehmigungs-Kontext, deshalb getrennt
  // von `approveResult`, sonst stünde der Genehmigungs-Banner mit leeren Werten im Dialog.
  const [standaloneSubstitution, setStandaloneSubstitution] = useState<AbsenceSubstitutionResult | null>(null);

  // Reject dialog state — der Ablehnungsgrund ist für den Mitarbeiter sichtbar
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [absenceToReject, setAbsenceToReject] = useState<AbsenceListDto | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    employeeId: '',
    absenceTypeId: '',
    startDate: '',
    endDate: '',
    notes: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  // Check if user is admin (Verwaltung = Admin role)
  const isAdmin = employee?.role === 'Verwaltung' || employee?.role === 'SuperAdmin';

  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          fetchAbsenceTypes(),
          fetchEmployees(),
        ]);

        // Load absences based on role
        if (!isAdmin && employee?.id) {
          await fetchAbsences({ employeeId: employee.id });
        } else {
          await fetchAbsences();
        }
      } finally {
        setInitialLoadComplete(true);
      }
    };

    loadData();
  }, [employee, isAdmin, fetchAbsences, fetchAbsenceTypes, fetchEmployees]);

  // Filter absences
  const filteredAbsences = absences.filter((absence) => {
    if (searchTerm) {
      const matchesSearch =
        absence.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        absence.absenceTypeName?.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matchesSearch) return false;
    }

    if (filterStatus !== 'all') {
      if (filterStatus === 'pending' && absence.approvalStatus !== 'Pending') return false;
      if (filterStatus === 'approved' && absence.approvalStatus !== 'Approved') return false;
      if (filterStatus === 'rejected' && absence.approvalStatus !== 'Rejected') return false;
    }

    if (filterCertificate === 'missing' && absence.certificateStatus !== 'Missing') return false;

    return true;
  });

  // Statistics
  const stats = {
    total: absences.length,
    approved: absences.filter(a => a.approvalStatus === 'Approved').length,
    pending: absences.filter(a => a.approvalStatus === 'Pending').length,
    rejected: absences.filter(a => a.approvalStatus === 'Rejected').length,
    certificateMissing: absences.filter(a => a.certificateStatus === 'Missing').length,
  };

  // Server warning that the absence overdraws an account; confirming sends it again.
  const [balanceWarning, setBalanceWarning] = useState<string | null>(null);
  const vacationBalances = useVacationStore((s) => s.balancesByEmployee);
  const fetchVacationBalance = useVacationStore((s) => s.fetchEmployeeBalance);
  const overtimeBalances = useOvertimeStore((s) => s.balancesByEmployee);
  const fetchOvertimeBalance = useOvertimeStore((s) => s.fetchEmployeeBalance);

  useEffect(() => {
    if (isAdmin && isSlideOverOpen && formData.employeeId) {
      void fetchVacationBalance(formData.employeeId);
      void fetchOvertimeBalance(formData.employeeId);
    }
  }, [isAdmin, isSlideOverOpen, formData.employeeId, fetchVacationBalance, fetchOvertimeBalance]);

  const selectedVacation = formData.employeeId ? vacationBalances[formData.employeeId] : undefined;
  const selectedOvertime = formData.employeeId ? overtimeBalances[formData.employeeId] : undefined;

  // Live preview of the chargeable days (FAFD-7): the administration asks for the chosen
  // employee, everyone else for themselves.
  const daysPreview = useWorkingDaysPreview(
    formData.startDate,
    formData.endDate,
    isAdmin ? formData.employeeId : undefined,
  );
  const selectedType = absenceTypes.find((tp) => tp.absenceTypeId === formData.absenceTypeId);
  const remainingBefore =
    !editingAbsence && selectedType?.countsAgainstVacation && selectedVacation
      ? selectedVacation.remainingDays - selectedVacation.pendingDays
      : null;

  // Absences of the administration are marked in the list (FACF-94).
  const backOfficeIds = useMemo(
    () => new Set(employees.filter((e) => isBackOfficeRole(e.role)).map((e) => e.id)),
    [employees],
  );

  // Auswahlliste für die Vertretung; die abwesende Person filtert der Dialog selbst heraus.
  const substituteOptions = useMemo(
    () =>
      employees
        .filter((e): e is typeof e & { id: string } => Boolean(e.id))
        .map((e) => ({ id: e.id, name: `${e.firstName} ${e.lastName}` })),
    [employees],
  );

  // Der Ergebnis-Dialog bedient beide Wege: Genehmigen mit Vertretung und nachträglich zuweisen.
  const substitutionForDialog = approveResult?.substitution ?? standaloneSubstitution;

  // Die Verwaltung legt Bestätigungen nach, die in Papierform im Büro abgegeben wurden.
  const handleCertificateUpload = async (absenceId: string, file: File) => {
    try {
      await uploadCertificate(absenceId, file, isAdmin);
    } catch {
      // Fehlermeldung kommt aus dem Store-Toast.
    }
  };

  const openCreateDialog = () => {
    setEditingAbsence(null);
    setFormData({
      employeeId: isAdmin ? '' : (employee?.id || ''),
      absenceTypeId: '',
      startDate: '',
      endDate: '',
      notes: '',
    });
    setIsSlideOverOpen(true);
  };

  const openEditDialog = async (absence: AbsenceListDto) => {
    setEditingAbsence(absence);

    // Load full absence data to get absenceTypeId
    const fullAbsence = await getAbsenceById(absence.absenceId || '');

    setFormData({
      employeeId: absence.employeeId || '',
      absenceTypeId: fullAbsence?.absenceTypeId || '',
      startDate: absence.startDate || '',
      endDate: absence.endDate || '',
      notes: fullAbsence?.notes || '',
    });
    setIsSlideOverOpen(true);
  };

  const openDeleteDialog = (absence: AbsenceListDto) => {
    setAbsenceToDelete({
      id: absence.absenceId || '',
      name: `${absence.employeeName} - ${absence.absenceTypeName} (${formatDate(absence.startDate)} - ${formatDate(absence.endDate)})`,
    });
    setIsDeleteDialogOpen(true);
  };

  const handleSave = async (acknowledgeBalanceWarning = false) => {
    // Validierung
    if (!formData.employeeId || !formData.absenceTypeId || !formData.startDate || !formData.endDate) {
      return;
    }

    // Permissions-Check für Mitarbeiter
    if (!isAdmin && formData.employeeId !== employee?.id) {
      return;
    }

    setIsSaving(true);
    try {
      if (editingAbsence) {
        const updateRequest: UpdateAbsenceRequest = {
          absenceTypeId: formData.absenceTypeId,
          startDate: formData.startDate,
          endDate: formData.endDate,
          notes: formData.notes || undefined,
          acknowledgeBalanceWarning,
        };
        await updateAbsence(editingAbsence.absenceId || '', updateRequest);
      } else {
        const createRequest: CreateAbsenceRequest = {
          employeeId: formData.employeeId,
          absenceTypeId: formData.absenceTypeId,
          startDate: formData.startDate,
          endDate: formData.endDate,
          notes: formData.notes || undefined,
          acknowledgeBalanceWarning,
        };
        await createAbsence(createRequest);
      }
      setIsSlideOverOpen(false);
    } catch (err) {
      // Balance warnings are confirmed here; every other error was already shown as a toast.
      const code = parseAxiosError(err).code ?? '';
      if (BALANCE_WARNING_CODES.has(code)) {
        setBalanceWarning(code);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!absenceToDelete) return;

    try {
      await deleteAbsence(absenceToDelete.id);
      setIsDeleteDialogOpen(false);
      setAbsenceToDelete(null);
    } catch (err) {
      console.error('Failed to delete absence:', err);
    }
  };

  // why: die Vertretung muss *vor* dem Genehmigen feststehen — danach sind die Einsätze bereits
  // als Konflikt markiert und die Verwaltung müsste ein zweites Mal ran (ADR-0016).
  const openApproveDialog = (absence: AbsenceListDto) => {
    if (!absence.absenceId) return;
    setAbsenceToApprove(absence);
    setIsApproveDialogOpen(true);
  };

  const handleApproveConfirm = async (substituteEmployeeId?: string) => {
    if (!absenceToApprove?.absenceId) return;

    setIsApproving(true);
    try {
      const result = await approveAbsence(
        absenceToApprove.absenceId, 'Approved', undefined, substituteEmployeeId);

      setIsApproveDialogOpen(false);
      setAbsenceToApprove(null);

      if (result) {
        const totalAffected = result.affectedPlanEntryIds.length + result.affectedPlanningEntryIds.length;
        if (totalAffected > 0 || result.substitution) {
          setApproveResult(result);
          setIsAffectedDialogOpen(true);
        }
      }
    } catch (err) {
      console.error('Failed to approve absence:', err);
    } finally {
      setIsApproving(false);
    }
  };

  const openSubstituteDialog = (absence: AbsenceListDto) => {
    if (!absence.absenceId) return;
    setAbsenceForSubstitute(absence);
    setIsSubstituteDialogOpen(true);
  };

  const handleSubstituteConfirm = async (substituteEmployeeId?: string) => {
    if (!absenceForSubstitute?.absenceId || !substituteEmployeeId) return;

    setIsApproving(true);
    try {
      const substitution = await assignSubstitute(absenceForSubstitute.absenceId, substituteEmployeeId);
      setIsSubstituteDialogOpen(false);
      setAbsenceForSubstitute(null);

      // Übersprungene Einsätze sind das Einzige, was die Verwaltung noch erledigen muss.
      if (substitution.skipped.length > 0) {
        setStandaloneSubstitution(substitution);
        setIsAffectedDialogOpen(true);
      }
    } catch (err) {
      console.error('Failed to assign substitute:', err);
    } finally {
      setIsApproving(false);
    }
  };

  const openRejectDialog = (absence: AbsenceListDto) => {
    if (!absence.absenceId) return;
    setAbsenceToReject(absence);
    setRejectionReason('');
    setIsRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!absenceToReject?.absenceId) return;

    setIsRejecting(true);
    try {
      await approveAbsence(absenceToReject.absenceId, 'Rejected', rejectionReason.trim() || undefined);
      setIsRejectDialogOpen(false);
      setAbsenceToReject(null);
    } catch (err) {
      console.error('Failed to reject absence:', err);
    } finally {
      setIsRejecting(false);
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const getStatusBadgeVariant = (status?: string | null): 'success' | 'warning' | 'danger' | 'default' => {
    if (status === 'Approved') return 'success';
    if (status === 'Pending') return 'warning';
    if (status === 'Rejected') return 'danger';
    return 'default';
  };

  const getStatusLabel = (status?: string | null) => {
    if (status === 'Approved') return t('absences:status.approved');
    if (status === 'Pending') return t('absences:status.pending');
    if (status === 'Rejected') return t('absences:status.rejected');
    return t('absences:status.unknown');
  };

  if (!initialLoadComplete) {
    return <Loading />;
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('absences:title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {isAdmin ? t('absences:subtitle.admin') : t('absences:subtitle.user')}
            </p>
          </div>
          <Button onClick={openCreateDialog} className="w-full sm:w-auto">
            <Plus className="w-4 h-4" strokeWidth={2} />
            {isAdmin ? t('absences:buttons.newAbsenceAdmin') : t('absences:buttons.newAbsenceUser')}
          </Button>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <div className="text-center">
            <p className="text-xs sm:text-sm text-text-secondary dark:text-slate-400">
              {t('absences:statistics.total')}
            </p>
            <p className="text-2xl sm:text-3xl font-bold text-text-primary dark:text-slate-50 mt-2">
              {stats.total}
            </p>
          </div>
        </Card>

        <Card>
          <div className="text-center">
            <p className="text-xs sm:text-sm text-text-secondary dark:text-slate-400">
              {t('absences:statistics.approved')}
            </p>
            <p className="text-2xl sm:text-3xl font-bold text-success mt-2">
              {stats.approved}
            </p>
          </div>
        </Card>

        <Card>
          <div className="text-center">
            <p className="text-xs sm:text-sm text-text-secondary dark:text-slate-400">
              {t('absences:statistics.pending')}
            </p>
            <p className="text-2xl sm:text-3xl font-bold text-warning mt-2">
              {stats.pending}
            </p>
          </div>
        </Card>

        <Card>
          <div className="text-center">
            <p className="text-xs sm:text-sm text-text-secondary dark:text-slate-400">
              {t('absences:statistics.rejected')}
            </p>
            <p className="text-2xl sm:text-3xl font-bold text-danger mt-2">
              {stats.rejected}
            </p>
          </div>
        </Card>

        <Card>
          <div className="text-center">
            <p className="text-xs sm:text-sm text-text-secondary dark:text-slate-400">
              {t('absences:statistics.certificateMissing')}
            </p>
            <p className="text-2xl sm:text-3xl font-bold text-warning mt-2 tabular-nums">
              {stats.certificateMissing}
            </p>
          </div>
        </Card>
      </div>

      {/* Fehlende Arztbestätigungen */}
      {stats.certificateMissing > 0 && filterCertificate !== 'missing' && (
        <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <FileWarning className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-600" strokeWidth={2} />
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-medium text-amber-900">
              {stats.certificateMissing === 1
                ? t('absences:certificate.adminMissingBannerOne')
                : t('absences:certificate.adminMissingBannerOther', { count: stats.certificateMissing })}
            </p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => setFilterCertificate('missing')}>
            {t('absences:filters.certificateMissing')}
          </Button>
        </div>
      )}

      {/* Filters */}
      <Card>
        <div className="flex flex-col md:flex-row gap-3 sm:gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-text-secondary" />
              <input
                type="text"
                placeholder={isAdmin ? t('absences:filters.searchPlaceholderAdmin') : t('absences:filters.searchPlaceholderUser')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-4 py-2 border border-border-primary dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-text-primary dark:text-slate-200 text-sm sm:text-base"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              aria-label={t('absences:filters.statusLabel')}
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 border border-border-primary dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-text-primary dark:text-slate-200 text-sm sm:text-base"
            >
              <option value="all">{t('absences:filters.allStatus')}</option>
              <option value="approved">{t('absences:status.approved')}</option>
              <option value="pending">{t('absences:status.pending')}</option>
              <option value="rejected">{t('absences:status.rejected')}</option>
            </select>
            <select
              value={filterCertificate}
              onChange={(e) => setFilterCertificate(e.target.value)}
              aria-label={t('absences:filters.certificateLabel')}
              className="flex-1 sm:flex-none px-3 sm:px-4 py-2 border border-border-primary dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-text-primary dark:text-slate-200 text-sm sm:text-base"
            >
              <option value="all">{t('absences:filters.allCertificates')}</option>
              <option value="missing">{t('absences:filters.certificateMissing')}</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Table or Empty State */}
      {error && initialLoadComplete ? (
        <Card>
          <EmptyState
            variant="error"
            title={t('absences:empty.errorTitle')}
            description={error}
            onRetry={() => fetchAbsences()}
          />
        </Card>
      ) : filteredAbsences.length === 0 ? (
        <EmptyState
          icon={<Calendar className="w-12 h-12" />}
          title={
            searchTerm || filterStatus !== 'all' || filterCertificate !== 'all'
              ? t('absences:empty.noResults')
              : t('absences:empty.noAbsences')
          }
          description={
            searchTerm || filterStatus !== 'all' || filterCertificate !== 'all'
              ? t('absences:empty.noResultsDescription')
              : t('absences:empty.noAbsencesDescription')
          }
          action={
            !searchTerm && filterStatus === 'all' && filterCertificate === 'all' ? (
              <Button onClick={openCreateDialog}>{t('absences:buttons.createAbsence')}</Button>
            ) : undefined
          }
        />
      ) : (
        <Card>
          <Table>
            <TableHead>
              <TableRow>
                {isAdmin && <TableHeader className="hidden sm:table-cell">{t('absences:table.employee')}</TableHeader>}
                <TableHeader>{t('absences:table.absence')}</TableHeader>
                <TableHeader className="hidden lg:table-cell">{t('absences:table.from')}</TableHeader>
                <TableHeader className="hidden lg:table-cell">{t('absences:table.to')}</TableHeader>
                <TableHeader className="hidden md:table-cell">{t('absences:table.workingDays')}</TableHeader>
                <TableHeader className="hidden sm:table-cell">{t('absences:table.status')}</TableHeader>
                <TableHeader>{t('absences:table.certificate')}</TableHeader>
                <TableHeader className="hidden md:table-cell">
                  {t('absences:table.substitute')}
                </TableHeader>
                {isAdmin && <TableHeader>{t('absences:table.actions')}</TableHeader>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAbsences.map((absence) => (
                <TableRow key={absence.absenceId}>
                  {isAdmin && (
                    <TableCell className="hidden sm:table-cell">
                      <div className="flex items-center gap-2 font-medium text-sm">
                        {absence.employeeName}
                        {backOfficeIds.has(absence.employeeId) && (
                          <Badge color="gray">{t('absences:table.backOffice')}</Badge>
                        )}
                      </div>
                    </TableCell>
                  )}
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {absence.absenceTypeColor && (
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: absence.absenceTypeColor }}
                        />
                      )}
                      <span className="text-sm sm:text-base font-medium">{absence.absenceTypeName}</span>
                    </div>
                    {/* Mobile: Show all condensed info */}
                    <div className="text-xs text-text-secondary mt-0.5 lg:hidden">
                      {formatDate(absence.startDate)} – {formatDate(absence.endDate)}
                      <span className="md:hidden tabular-nums">
                        {' · '}{formatDays(chargeableDays(absence))} {t('absences:table.workingDays')}
                      </span>
                    </div>
                    {isAdmin && (
                      <div className="text-xs text-text-secondary sm:hidden mt-0.5">
                        {absence.employeeName}
                      </div>
                    )}
                    {/* Mobile: Status inline */}
                    <div className="sm:hidden mt-1">
                      <Badge variant={getStatusBadgeVariant(absence.approvalStatus)}>
                        {getStatusLabel(absence.approvalStatus)}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {formatDate(absence.startDate)}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    {formatDate(absence.endDate)}
                  </TableCell>
                  <TableCell className="hidden md:table-cell tabular-nums">
                    {formatDays(chargeableDays(absence))}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <Badge variant={getStatusBadgeVariant(absence.approvalStatus)}>
                      {getStatusLabel(absence.approvalStatus)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <CertificateCell
                      status={absence.certificateStatus}
                      documentId={absence.certificateDocumentId}
                      uploading={uploadingCertificateFor === absence.absenceId}
                      onUpload={
                        isAdmin ? (file) => handleCertificateUpload(absence.absenceId, file) : undefined
                      }
                      onView={() =>
                        absence.certificateDocumentId && openCertificate(absence.certificateDocumentId)
                      }
                    />
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {absence.substituteEmployeeName ? (
                      <Badge variant="info">{absence.substituteEmployeeName}</Badge>
                    ) : isAdmin && absence.approvalStatus === 'Approved' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openSubstituteDialog(absence)}
                        className="text-[12px] px-1.5"
                      >
                        {t('absences:substitute.assign')}
                      </Button>
                    ) : (
                      <span className="text-[12px] text-slate-400">–</span>
                    )}
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      <div className="flex items-center gap-1 sm:gap-2">
                        {/* Approval Buttons (bei Pending Status) */}
                        {absence.approvalStatus === 'Pending' && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openApproveDialog(absence)}
                              title={t('absences:buttons.approve')}
                              className="p-1 sm:p-2"
                            >
                              <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openRejectDialog(absence)}
                              title={t('absences:buttons.reject')}
                              className="p-1 sm:p-2"
                            >
                              <X className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600" />
                            </Button>
                          </>
                        )}

                        {/* Standard Actions */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(absence)}
                          title={t('absences:buttons.edit')}
                          className="p-1 sm:p-2"
                        >
                          <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openDeleteDialog(absence)}
                          title={t('absences:buttons.delete')}
                          className="p-1 sm:p-2"
                        >
                          <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* SlideOver for Create/Edit */}
      <SlideOver
        isOpen={isSlideOverOpen}
        onClose={() => setIsSlideOverOpen(false)}
        title={editingAbsence ? t('absences:form.titleEdit') : t('absences:form.titleNew')}
        size="lg"
        footer={
          <SlideOverFooter
            onCancel={() => setIsSlideOverOpen(false)}
            onConfirm={() => void handleSave()}
            confirmText={editingAbsence ? t('absences:buttons.save') : t('absences:buttons.create')}
            cancelText={t('absences:buttons.cancel')}
            loading={isSaving}
          />
        }
      >
        <div className="space-y-4">
          {/* Employee Selection */}
          {isAdmin && (
            <FormField label={t('absences:form.employeeLabel')} required helperText={editingAbsence ? t('absences:form.employeeLocked') : undefined}>
              <select
                value={formData.employeeId}
                onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                className="w-full px-3 py-2 border border-border-primary dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-text-primary dark:text-slate-200 text-sm sm:text-base focus:ring-2 focus:ring-primary focus:border-transparent"
                required
                disabled={!!editingAbsence}
                aria-label={t('absences:form.employeeLabel')}
              >
                <option value="">{t('absences:form.employeeSelect')}</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.firstName} {emp.lastName}
                  </option>
                ))}
              </select>
              {(selectedVacation || selectedOvertime) && (
                <p className="mt-1.5 flex flex-wrap items-center gap-x-1 text-[12px] text-slate-600 dark:text-slate-400 tabular-nums">
                  {selectedVacation &&
                    t('absences:form.balanceVacation', {
                      days: formatDays(selectedVacation.remainingDays - selectedVacation.pendingDays),
                    })}
                  {selectedVacation && (
                    <RoundedDaysHint
                      value={selectedVacation.remainingDays - selectedVacation.pendingDays}
                      unrounded={
                        selectedVacation.remainingDaysUnrounded === null
                          ? null
                          : selectedVacation.remainingDaysUnrounded - selectedVacation.pendingDays
                      }
                    />
                  )}
                  {selectedVacation && selectedOvertime && ' · '}
                  {selectedOvertime &&
                    t('absences:form.balanceOvertime', { hours: formatSignedHours(selectedOvertime.balanceHours) })}
                </p>
              )}
            </FormField>
          )}

          {/* Absence Type */}
          <FormField label={t('absences:form.absenceTypeLabel')} required>
            <select
              value={formData.absenceTypeId}
              onChange={(e) => setFormData({ ...formData, absenceTypeId: e.target.value })}
              className="w-full px-3 py-2 border border-border-primary dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-text-primary dark:text-slate-200 text-sm sm:text-base focus:ring-2 focus:ring-primary focus:border-transparent"
              required
              aria-label={t('absences:form.absenceTypeLabel')}
            >
              <option value="">{t('absences:form.absenceTypeSelect')}</option>
              {absenceTypes.filter(t => t.isActive).map((type) => (
                <option key={type.absenceTypeId} value={type.absenceTypeId}>
                  {type.name}
                </option>
              ))}
            </select>
          </FormField>

          {/* Date Range */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label={t('absences:form.fromLabel')}
              required
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              className="text-sm sm:text-base"
            />
            <FormField
              label={t('absences:form.toLabel')}
              required
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
              className="text-sm sm:text-base"
            />
          </div>

          <WorkingDaysPreview
            workingDays={daysPreview.workingDays}
            loading={daysPreview.loading}
            remainingVacation={remainingBefore}
          />

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-text-primary dark:text-slate-200 mb-1">
              {t('absences:form.notesLabel')}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-border-primary dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-text-primary dark:text-slate-200 text-sm sm:text-base focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder={t('absences:form.notesPlaceholder')}
            />
          </div>
        </div>
      </SlideOver>

      {/* Balance warning: the absence overdraws vacation or overtime — book anyway? */}
      <ConfirmDialog
        isOpen={balanceWarning !== null}
        onClose={() => setBalanceWarning(null)}
        onConfirm={() => {
          setBalanceWarning(null);
          void handleSave(true);
        }}
        title={t('absences:balanceWarning.title')}
        message={t(
          balanceWarning === 'planning.absences.overtimeBalanceWarning'
            ? 'absences:balanceWarning.overtime'
            : 'absences:balanceWarning.vacation',
        )}
        confirmText={t('absences:balanceWarning.confirm')}
        cancelText={t('absences:buttons.cancel')}
        variant="warning"
        loading={isSaving}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setAbsenceToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t('absences:deleteDialog.title')}
        message={`${t('absences:deleteDialog.message')}\n\n${absenceToDelete?.name}\n\n${t('common:confirmations.cannotUndo')}`}
        confirmText={t('absences:buttons.delete')}
        cancelText={t('absences:buttons.cancel')}
        variant="danger"
      />

      {/* Affected Entries Dialog (after approval) */}
      <Modal
        isOpen={isAffectedDialogOpen}
        onClose={() => {
          setIsAffectedDialogOpen(false);
          setApproveResult(null);
          setStandaloneSubstitution(null);
        }}
        title={approveResult ? t('absences:approvalDialog.title') : t('absences:substitute.dialogTitle')}
      >
        {(approveResult || standaloneSubstitution) && (
          <div className="space-y-4">
            {/* Genehmigungs-Kontext nur, wenn der Dialog aus dem Genehmigen kommt. */}
            {approveResult && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    {t('absences:approvalDialog.affectedEntriesTitle')}
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1" dangerouslySetInnerHTML={{
                    __html: t('absences:approvalDialog.approvedMessage', {
                      employeeName: approveResult.absence.employeeName,
                      startDate: formatDate(approveResult.absence.startDate),
                      endDate: formatDate(approveResult.absence.endDate)
                    })
                  }} />
                </div>
              </div>
            )}

            {substitutionForDialog && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
                <p className="text-[13px] font-medium text-emerald-900">
                  {t('absences:approvalDialog.substituteSummary', {
                    name: substitutionForDialog.substituteEmployeeName,
                    count:
                      substitutionForDialog.movedPlanEntryCount +
                      substitutionForDialog.movedTaskCount,
                  })}
                </p>
              </div>
            )}

            {substitutionForDialog && substitutionForDialog.skipped.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="text-[13px] font-medium text-amber-900">
                  {t('absences:approvalDialog.skippedTitle')}
                </p>
                <ul className="mt-1.5 space-y-1">
                  {substitutionForDialog.skipped.map((skip, index) => (
                    <li
                      key={`${skip.date}-${skip.taskName}-${index}`}
                      className="text-[12px] text-amber-800 tabular-nums"
                    >
                      {formatDate(skip.date)} · {skip.taskName} ·{' '}
                      {t(`absences:substitute.skipReason.${skip.reason}`)}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="space-y-2">
              {approveResult && approveResult.affectedPlanEntryIds.length > 0 && (
                <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <span className="text-sm font-bold text-blue-700 dark:text-blue-300">
                      {approveResult.affectedPlanEntryIds.length}
                    </span>
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {approveResult.affectedPlanEntryIds.length === 1
                      ? t('absences:approvalDialog.planConflictSingular')
                      : t('absences:approvalDialog.planConflictPlural')}
                  </span>
                </div>
              )}
              {approveResult && approveResult.affectedPlanningEntryIds.length > 0 && (
                <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                    <span className="text-sm font-bold text-orange-700 dark:text-orange-300">
                      {approveResult.affectedPlanningEntryIds.length}
                    </span>
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {approveResult.affectedPlanningEntryIds.length === 1
                      ? t('absences:approvalDialog.dailyPlanConflictSingular')
                      : t('absences:approvalDialog.dailyPlanConflictPlural')}
                  </span>
                </div>
              )}
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('absences:approvalDialog.reassignInfo')}
            </p>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="secondary"
                className="w-full sm:w-auto"
                onClick={() => {
                  setIsAffectedDialogOpen(false);
                  setApproveResult(null);
                }}
              >
                {t('absences:buttons.later')}
              </Button>
              <Button
                className="w-full sm:w-auto"
                onClick={() => {
                  setIsAffectedDialogOpen(false);
                  setApproveResult(null);
                  navigate('/planning');
                }}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {t('absences:buttons.toPlanning')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Reject Dialog */}
      <Modal
        isOpen={isRejectDialogOpen}
        onClose={() => {
          setIsRejectDialogOpen(false);
          setAbsenceToReject(null);
        }}
        title={t('absences:rejectDialog.title')}
      >
        <div className="space-y-4">
          {absenceToReject && (
            <p className="text-[13px] text-slate-700">
              {absenceToReject.employeeName} · {absenceToReject.absenceTypeName}{' '}
              <span className="tabular-nums text-slate-500">
                ({formatDate(absenceToReject.startDate)} – {formatDate(absenceToReject.endDate)})
              </span>
            </p>
          )}

          <TextArea
            label={t('absences:rejectDialog.reasonLabel')}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            rows={3}
            placeholder={t('absences:rejectDialog.reasonPlaceholder')}
          />

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button
              variant="secondary"
              className="w-full sm:w-auto"
              onClick={() => {
                setIsRejectDialogOpen(false);
                setAbsenceToReject(null);
              }}
            >
              {t('absences:buttons.cancel')}
            </Button>
            <Button
              variant="danger"
              className="w-full sm:w-auto"
              onClick={handleRejectConfirm}
              disabled={isRejecting}
            >
              {t('absences:rejectDialog.confirm')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Genehmigen mit optionaler Vertretung (ADR-0016) */}
      <SubstitutePickerDialog
        isOpen={isApproveDialogOpen}
        onClose={() => {
          setIsApproveDialogOpen(false);
          setAbsenceToApprove(null);
        }}
        onConfirm={handleApproveConfirm}
        title={t('absences:substitute.approveDialogTitle')}
        confirmText={t('absences:buttons.approve')}
        allowEmpty
        employees={substituteOptions}
        excludeEmployeeId={absenceToApprove?.employeeId}
        saving={isApproving}
      />

      {/* Vertretung nachträglich zuweisen */}
      <SubstitutePickerDialog
        isOpen={isSubstituteDialogOpen}
        onClose={() => {
          setIsSubstituteDialogOpen(false);
          setAbsenceForSubstitute(null);
        }}
        onConfirm={handleSubstituteConfirm}
        title={t('absences:substitute.dialogTitle')}
        confirmText={t('absences:substitute.assign')}
        allowEmpty={false}
        employees={substituteOptions}
        excludeEmployeeId={absenceForSubstitute?.employeeId}
        saving={isApproving}
      />
    </div>
  );
}

